import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { debouncedReSyncAll } from '../db/sync/replication';

export interface PendingItem {
  id: string;
  collection: string;
  collectionLabel: string;
  label: string;
  action: 'create' | 'update' | 'delete';
}

interface SyncError {
  message: string;
  timestamp: number;
  collection: string;
}

interface SyncState {
  navigatorOnline: boolean;
  autoSyncEnabled: boolean;
  isOnline: boolean; // Virtual online state: navigatorOnline && autoSyncEnabled
  isSyncing: boolean;
  pendingCount: number;
  pendingItems: PendingItem[];
  conflictCount: number;
  lastSyncAt: number | null;
  errors: SyncError[];

  setOnline: (navigatorOnline: boolean) => void;
  setAutoSyncEnabled: (enabled: boolean) => void;
  toggleAutoSync: () => void;
  setSyncing: (isSyncing: boolean) => void;
  setPendingCount: (count: number) => void;
  setPendingItems: (items: PendingItem[]) => void;
  setConflictCount: (count: number) => void;
  addError: (error: Omit<SyncError, 'timestamp'>) => void;
  clearErrors: () => void;
  setLastSyncAt: (timestamp: number) => void;
}

export const useSyncStore = create<SyncState>()(
  devtools(
    persist(
      (set) => {
        const initialNavOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
        const initialAutoSync = true;

        return {
          navigatorOnline: initialNavOnline,
          autoSyncEnabled: initialAutoSync,
          isOnline: initialNavOnline && initialAutoSync,
          isSyncing: false,
          pendingCount: 0,
          pendingItems: [],
          conflictCount: 0,
          lastSyncAt: null,
          errors: [],

          setOnline: (navigatorOnline) =>
            set((state) => {
              const newIsOnline = navigatorOnline && state.autoSyncEnabled;
              if (newIsOnline && !state.isOnline) {
                setTimeout(() => debouncedReSyncAll('network_online'), 100);
              }
              return {
                navigatorOnline,
                isOnline: newIsOnline,
              };
            }),

          setAutoSyncEnabled: (autoSyncEnabled) =>
            set((state) => {
              const newIsOnline = state.navigatorOnline && autoSyncEnabled;
              if (newIsOnline && !state.isOnline) {
                setTimeout(() => debouncedReSyncAll('manual'), 100);
              }
              return {
                autoSyncEnabled,
                isOnline: newIsOnline,
              };
            }),

          toggleAutoSync: () =>
            set((state) => {
              const newAutoSync = !state.autoSyncEnabled;
              const newIsOnline = state.navigatorOnline && newAutoSync;
              if (newIsOnline && !state.isOnline) {
                setTimeout(() => debouncedReSyncAll('manual'), 100);
              }
              return {
                autoSyncEnabled: newAutoSync,
                isOnline: newIsOnline,
              };
            }),

          setSyncing: (isSyncing) => set({ isSyncing }),
          setPendingCount: (count) => set({ pendingCount: count }),
          setPendingItems: (items) => set({ pendingItems: items, pendingCount: items.length }),
          setConflictCount: (count) => set({ conflictCount: count }),

          addError: (error) =>
            set((state) => ({
              errors: [
                ...state.errors,
                { ...error, timestamp: Date.now() },
              ],
            })),

          clearErrors: () => set({ errors: [] }),
          setLastSyncAt: (timestamp) => set({ lastSyncAt: timestamp }),
        };
      },
      {
        name: 'depilaway-sync-settings',
        partialize: (state) => ({
          autoSyncEnabled: state.autoSyncEnabled,
          lastSyncAt: state.lastSyncAt,
          errors: state.errors,
          pendingItems: state.pendingItems,
          pendingCount: state.pendingCount,
        }),
        onRehydrateStorage: () => (state) => {
          if (state) {
            const navOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
            state.navigatorOnline = navOnline;
            state.isOnline = navOnline && state.autoSyncEnabled;
          }
        },
      }
    ),
    { name: 'SyncStore' }
  )
);

// Centralized browser online/offline listeners
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useSyncStore.getState().setOnline(true);
  });

  window.addEventListener('offline', () => {
    useSyncStore.getState().setOnline(false);
  });
}
